import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ListShape from './prop_shapes/list'
import { addSupplierToListPath, listsPath, removeSupplierFromListPath } from '../routes/api'
import { api } from 'components/api'
import ListForm from './list_selector/ListForm'
import Modal from 'components/common/modal/Modal'

const StopClickPropagation = ({ children, excludeClosestCss }) => {
  const handleClick = (e) => {
    // Only stop propagation if the click didn't originate from the anchor button
    if (!e.target.closest(excludeClosestCss)) {
      e.stopPropagation()
    }
  }

  return (
    <div onClick={handleClick}>
      {children}
    </div>
  )
}

StopClickPropagation.propTypes = {
  children: PropTypes.node,
  excludeClosestCss: PropTypes.string
}

StopClickPropagation.defaultProps = {
  children: null,
  excludeClosestCss: null
}

function useListenForOutsideClicks (
  domNodeRef,
  { shouldListen, onClickOutside } = { shouldListen: true, onClickOutside: () => {} }
) {
  // Keep a ref to the current callback so we don't have to call useEffect every time the function ref changes.
  // This ensures we always call the latest callback.
  const callbackRef = useRef(onClickOutside)
  callbackRef.current = onClickOutside

  useEffect(() => {
    // Don't create a listener if we shouldn't be listening.
    if (!shouldListen) return

    const isInListSelector = (element) => {
      return !!(domNodeRef?.current && domNodeRef.current.contains(element))
    }

    // Keeping the listener inside the useEffect ensures that `document.removeEventListener` is called with the correct
    // function reference.
    const listenForOutsideClicks = (e) => {
      if (domNodeRef?.current && (!isInListSelector(e.target))) {
        callbackRef.current()
      }
    }

    document.addEventListener('click', listenForOutsideClicks)

    return () => {
      document.removeEventListener('click', listenForOutsideClicks)
    }
  }, [shouldListen])
}

export const ListSelector = (props) => {
  const listSelectorRef = useRef()
  const [dropdownVisible, setDropdownVisible] = useState(false)

  useListenForOutsideClicks(listSelectorRef, {
    shouldListen: dropdownVisible,
    onClickOutside: () => {
      // Clicking outside the list selector should *close* the dropdown.
      setDropdownVisible(false)
      props.onClose && props.onClose()
    }
  })

  const handleDropdownButtonClick = () => {
    setDropdownVisible(!dropdownVisible)
    if (dropdownVisible) {
      props.onClose && props.onClose()
    } else {
      props.onOpen && props.onOpen()
    }
  }

  const handleSupplierSelection = (e) => {
    const clickedListId = e.target.name
    const selectedList = props.lists.find(
      (list) => typeof list.id === 'number' ? list.id === parseInt(clickedListId) : list.id === clickedListId
    )

    if (props.onSelectList) props.onSelectList(selectedList)
  }

  const includesSupplier = (list, supplier) => {
    return list.suppliers.find((listSupplier) => listSupplier.id === supplier.id) !== undefined
  }

  const CreateNewListButton = (
    <button className='button create-list-button'>
      Create new list
      <i className='far fa-plus float-right' />
    </button>
  )

  const renderListsDropdown = (lists, supplier) => {
    return (
      <div className='lists-dropdown'>
        <div className='lists-container'>
          {
            lists.map((list) => {
              const selected = includesSupplier(list, supplier)

              return (
                <div key={list.id} className={selected ? 'selected' : ''}>
                  <label>
                    {list.name}
                    <input
                      type='checkbox'
                      name={list.id}
                      checked={selected}
                      onChange={handleSupplierSelection}
                    />
                  </label>
                </div>
              )
            })
          }
        </div>
        <StopClickPropagation excludeClosestCss='.create-list-button'>
          <Modal anchor={CreateNewListButton}>
            <div id='lists-modal' className='lists-modal pro-rounded'>
              <ListForm supplier={props.supplier} onCreateList={props.onCreateList} />
            </div>
          </Modal>
        </StopClickPropagation>
      </div>
    )
  }

  return (
    <div className={`list-selector grid-x bottom-margin ${dropdownVisible ? 'open' : 'closed'}`} ref={listSelectorRef}>
      <div className='cell auto'>
        <button className='open-button button' onClick={handleDropdownButtonClick}>
          Add to Supplier List
          <i className='fas fa-sort float-right' />
        </button>
        {dropdownVisible &&
          renderListsDropdown(props.lists, props.supplier)}
      </div>
    </div>
  )
}

ListSelector.propTypes = {
  supplier: PropTypes.shape({ id: PropTypes.number }),
  lists: PropTypes.arrayOf(ListShape),
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onSelectList: PropTypes.func,
  onCreateList: PropTypes.func
}

ListSelector.defaultProps = {
  supplier: {},
  lists: [],
  onOpen: null,
  onClose: null,
  onSelectList: null,
  onCreateList: null
}

// Hook for all the API calls that handles race conditions. Rules are this:
// - When `getLists()` is called, only the last result is used.
// - When there are any in-flight mutations, the results of `getLists` is discarded.
// - After all in-flight mutations are done, `getLists` is called to make sure the latest results are used.
function useListsApi () {
  // It's necessary to keep track of the current promise because there might be race conditions when calling the API
  // multiple times.
  const currentFetchPromise = useRef(null)
  // If this count is zero, there are no in-flight mutations.
  const mutationCount = useRef(0)
  const componentIsMounted = useRef(true)

  const [isLoading, setIsLoading] = useState(false)
  const [lists, setLists] = useState(null)

  const getLists = () => {
    // There are in-flight mutations, so we don't want to fetch the latest lists.
    // Latest lists will be re-fetched after the mutations are done.
    if (mutationCount.current > 0 || !componentIsMounted.current) return

    const currentFetchReference = api.get({ url: listsPath() }).then(({ data }) => data)
    currentFetchPromise.current = currentFetchReference

    if (lists === null) setIsLoading(true)

    // Only updates the lists when this is the latest call and when there aren't any other mutations in progress.
    currentFetchReference
      .then(lists => {
        // This promise was the last called promise, we can modify the list + set loading to false.
        if (currentFetchPromise.current === currentFetchReference && mutationCount.current === 0) {
          if (componentIsMounted.current) {
            setLists(lists)
            setIsLoading(false)
          }
          currentFetchPromise.current = null
        }
      })
  }

  // Update function that's functional setState, but looks up the id of the list before setting it.
  // We do this because the reference to the list might be out of date, always want to use the latest one.
  const updateListWithId = (id, updateFunc) => {
    if (!componentIsMounted.current) return
    setLists(lists => lists.map(list => list.id === id ? updateFunc(list) : list))
  }

  // Wraps the mutations in a promise that updates the `mutationCount` ref.
  // When the promise resolves and the `mutatioNCount` is 0, we refetch the lists.
  // Does this on both `then` and `catch`.
  const refetchAfterAllMutationsFinish = (mutation) => {
    mutationCount.current = mutationCount.current + 1

    const handlePromiseResult = (data) => {
      mutationCount.current = mutationCount.current - 1
      if (mutationCount.current === 0) {
        getLists()
      }
      return data
    }

    // If there is an in-flight fetch, it needs to be discarded. It will return out-of-date data.
    if (currentFetchPromise.current !== null) currentFetchPromise.current = null

    return mutation.then(handlePromiseResult).catch(handlePromiseResult)
  }

  const addSupplierToList = (selectedList, supplier) => {
    return refetchAfterAllMutationsFinish(
      api.patchWithCsrf({
        url: addSupplierToListPath(selectedList.id),
        data: {
          supplier_id: supplier.id
        }
      })
        .then(({ data }) => data)
    )
  }

  const removeSupplierFromList = (selectedList, supplier) => {
    return refetchAfterAllMutationsFinish(
      api.patchWithCsrf({
        url: removeSupplierFromListPath(selectedList.id),
        data: { supplier_id: supplier.id }
      })
        .then(({ data }) => data)
    )
  }

  const createListWithSupplier = (listName, supplierId) => {
    return refetchAfterAllMutationsFinish(
      api.postWithCsrf({
        url: listsPath(),
        data: {
          list: {
            name: listName,
            supplier_ids: [
              supplierId
            ]
          }
        }
      })
        .then(({ data }) => data)
    )
  }

  return {
    isLoading,
    lists: lists || [],
    getLists,
    addSupplierToList,
    removeSupplierFromList,
    updateListWithId,
    createListWithSupplier
  }
}
const ListSelectorWithApiCall = (props) => {
  const { lists, getLists, addSupplierToList, removeSupplierFromList, updateListWithId, createListWithSupplier } = useListsApi()

  const listHasSupplier = (list, supplier) => list.suppliers.find(listSupplier => listSupplier.id === supplier.id) !== undefined

  const toggleSupplierInList = (list, supplier) => {
    if (listHasSupplier(list, supplier)) {
      return { ...list, suppliers: list.suppliers.filter(listSupplier => listSupplier.id !== supplier.id) }
    } else {
      return { ...list, suppliers: [...list.suppliers, { ...supplier }] }
    }
  }

  const optimisticallyUpdateLists = (selectedList) => {
    updateListWithId(selectedList.id, (foundList) => toggleSupplierInList(foundList, props.supplier))

    if (listHasSupplier(selectedList, props.supplier)) {
      removeSupplierFromList(selectedList, props.supplier)
    } else {
      addSupplierToList(selectedList, props.supplier)
    }
  }

  return (
    <ListSelector
      {...props}
      lists={lists}
      onOpen={() => getLists()}
      onSelectList={(list) => optimisticallyUpdateLists(list)}
      onCreateList={createListWithSupplier}
    />
  )
}

ListSelectorWithApiCall.propTypes = {
  supplier: PropTypes.shape({ id: PropTypes.number })
}

ListSelectorWithApiCall.defaultProps = {
  supplier: {}
}

export default ListSelectorWithApiCall
